import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { DataTableRowClickEvent } from "primereact/datatable";
import { useCallback, useMemo, useState } from "react";
import { RTU } from "../../types/RTU";
import { formatProducerName, isMissing } from "../../utils/rtuDefaults";
import SimpleLoader from "../common/SimpleLoader";
import PopupProducerDevices from "./PopupProducerDevices";

/** Display-only label; never written onto RTUs or sent in API payloads. */
const UNASSIGNED_DISPLAY = "Unassigned";

type ProducerSelection =
  | { kind: "missing" }
  | { kind: "value"; provider: string };

type ProducerRow = {
  groupId: string;
  displayName: string;
  count: number;
  percentage: string;
  selection: ProducerSelection;
};

const selectionForProvider = (provider: string | null | undefined): ProducerSelection =>
  isMissing(provider) ? { kind: "missing" } : { kind: "value", provider: String(provider) };

const encodeProducerGroupId = (selection: ProducerSelection): string =>
  selection.kind === "missing"
    ? JSON.stringify(["missing"])
    : JSON.stringify(["value", selection.provider]);

const displayNameForSelection = (selection: ProducerSelection): string =>
  selection.kind === "missing" ? UNASSIGNED_DISPLAY : formatProducerName(selection.provider);

export const DevicesByClient = ({ rtus }: { rtus: RTU[] }) => {
  const totalDevices = rtus.length;

  const dataForTable = useMemo((): ProducerRow[] => {
    const groups = new Map<string, { selection: ProducerSelection; count: number }>();

    rtus.forEach((rtu) => {
      const selection = selectionForProvider(rtu.provider);
      const groupId = encodeProducerGroupId(selection);
      const existing = groups.get(groupId);
      if (existing) {
        existing.count += 1;
      } else {
        groups.set(groupId, { selection, count: 1 });
      }
    });

    return Array.from(groups.entries()).map(([groupId, { selection, count }]) => ({
      groupId,
      displayName: displayNameForSelection(selection),
      count,
      percentage: totalDevices > 0 ? ((count / totalDevices) * 100).toFixed(2) : "0.00",
      selection,
    }));
  }, [rtus, totalDevices]);

  const [producerPopupOpen, setProducerPopupOpen] = useState(false);
  const [selectedProducer, setSelectedProducer] = useState<ProducerSelection | null>(null);

  const rtusForSelectedProducer = useMemo(() => {
    if (!selectedProducer) {
      return [];
    }
    if (selectedProducer.kind === "missing") {
      return rtus.filter((rtu) => isMissing(rtu.provider));
    }
    return rtus.filter((rtu) => rtu.provider === selectedProducer.provider);
  }, [rtus, selectedProducer]);

  const openProducerPopup = useCallback((selection: ProducerSelection) => {
    setSelectedProducer(selection);
    setProducerPopupOpen(true);
  }, []);

  const closeProducerPopup = useCallback(() => {
    setProducerPopupOpen(false);
    setSelectedProducer(null);
  }, []);

  const onProducerRowClick = useCallback(
    (event: DataTableRowClickEvent) => {
      const row = event.data as ProducerRow | undefined;
      if (!row?.selection) {
        return;
      }
      openProducerPopup(row.selection);
    },
    [openProducerPopup]
  );

  const popupTitle = selectedProducer ? displayNameForSelection(selectedProducer) : "";

  return (
    <>
      <DataTable
        value={dataForTable}
        dataKey="groupId"
        className="devices-by-producer-datatable"
        tableStyle={{ width: "100%", minWidth: "max-content" }}
        showGridlines
        emptyMessage={<SimpleLoader />}
        onRowClick={onProducerRowClick}
        rowClassName={() => "devices-by-producer-datatable__row"}
      >
        <Column
          field="displayName"
          header="Producer"
          style={{ color: "#1c80cf", fontWeight: "bold", whiteSpace: "nowrap" }}
        />
        <Column
          field="count"
          header="Devices"
          style={{ whiteSpace: "nowrap" }}
          bodyStyle={{ textAlign: "center" }}
        />
        <Column
          field="percentage"
          header="Percentage (%)"
          style={{ whiteSpace: "nowrap" }}
          body={(rowData) => `${rowData.percentage}%`}
          bodyStyle={{ textAlign: "center" }}
        />
      </DataTable>

      {producerPopupOpen && selectedProducer ? (
        <PopupProducerDevices
          producerName={popupTitle}
          rtus={rtusForSelectedProducer}
          onClose={closeProducerPopup}
        />
      ) : null}
    </>
  );
};
